import { useRef, useEffect, useState } from "react";
import React from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import "../../../App.css";
import "../../../css/bootstrap.scss";
import signup_banner from "../../../images/fx-rates.png";
import { Link } from "react-router-dom";
import Header from "../../Includes/Header";
import ReCAPTCHA from "react-google-recaptcha";
import BackButton from "../../Includes/BackButton";
import config from "../../../config";
import axios, { AxiosError } from "axios";
import { Field, reduxForm, InjectedFormProps, FormName } from "redux-form";
import renderField from "../../renderField";
import DobField from "../../DobField";
import { connect } from "react-redux";
import { setEmail, setfirstName, setlastName, setRegistrationDob, setwizardPageNumber, setWizardStepValidation, setUserID } from "../../authslice";
import { setEmail as setEntityEmail } from "../../EntityAccountReducer";
import { RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import { ToastBox, notifyError } from "MyComponents/Toaster";
import Spinner from "react-bootstrap/Spinner";
import {
  isEmailExist,
  validateEmail,
  getUserID,
  getUserData,
  isRecaptchaVerified,
} from "../../../Utils/user";
import { checkExistingRelationship } from "../../../Utils/entityLink";
import LinkEntityVerification from "./LinkEntityVerification";
import Spacer from "MyComponents/UI/Spacer";
import { useParams } from "react-router-dom";

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  dob: string;
}

interface Props { }

const RegisterPersonalAccount: React.FC<
  Props & InjectedFormProps<FormData, Props>
> = (props) => {
  const { t } = useTranslation();
  const { handleSubmit } = props;
  const dispatch = useDispatch();
  const page = useSelector((state: RootState) => state.auth.wizardPageNumber);
  const theme = useSelector((state: RootState) => state.theme.theme);
  const invitationId = useSelector((state: RootState) => state.auth.invitationId);
  const entityId = useSelector((state: RootState) => state.auth.entityId);
  const entityName = useSelector((state: RootState) => state.auth.entityName);
  const [isChecked, setIsChecked] = useState<boolean | false>(false);
  const [emailValidation, setEmailValidation] = useState<boolean | false>(false);
  const [businessAccountConflict, setBusinessAccountConflict] = useState<boolean | false>(false);
  const [conflictEmail, setConflictEmail] = useState<string>("");
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean | false>(false);
  const [loading, setLoading] = useState<boolean | false>(false);
  const [validated, setValidated] = useState<boolean | false>(false);
  const [alreadyLinkedName, setAlreadyLinkedName] = useState<string | null>(null);
  const [linkFlow, setLinkFlow] = useState<{ userId: string; email: string; entityName: string | null } | null>(null);

  // Which entity relationship this attempt would create — derived from the URL,
  // same rule the backend's resolveEntityLinkage uses (invitationId -> employee, entityId -> client).
  const relationshipKey = invitationId ? "relationship_employee" : "relationship_client";
  const relationshipNoun = t(`register.${relationshipKey}`);

  const recaptcha = useRef<ReCAPTCHA>(null);
  let isEmailVerified = "";

  const site_key: string | undefined = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const { platform } = useParams();
  const currentPlatform = platform || "default";
  localStorage.setItem("platform", currentPlatform);
  console.log("User redirected from platform:", currentPlatform);

  const handleRecaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
    if (value) {
      setValidated(false);
    }
  };

  const startLoading = () => {
    setLoading(true);
  };

  const handleChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setEmailValidation(false);
      setBusinessAccountConflict(false);
      setAlreadyLinkedName(null);
    }
  };

  useEffect(() => {
    if (isEmailVerified === "1") {
      setEmailValidation(true);
    }
  }, [isEmailVerified]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
  };

  const registerMiniOverridePassword = async (email: string) => {
    try {
      const getRegisterMiniOverridePassword = await axios
        .post(config.NODE_API_BASE_URL + "/wso2/registerMiniOverridePassword", { email })
        .catch((reason: AxiosError<{ additionalInfo: string }>) => {
          if (reason.response!.status != 200) {
            notifyError("OTP Sending Failed");
          }
          console.log(reason.message);
        });
      if (getRegisterMiniOverridePassword?.data?.status) {
        return getRegisterMiniOverridePassword.data.status;
      } else {
        return 0;
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  const submitForm = async (formValues: FormData) => {
    setIsSubmitted(true);
    const captchaValue: any = recaptcha.current?.getValue();
    if (!captchaValue) {
      setValidated(true);
      return;
    }

    try {
      const recaptchaResponse: any = await isRecaptchaVerified(captchaValue);
      if (!recaptchaResponse) {
        notifyError("Recaptcha Failed");
        setValidated(true);
        return;
      }

      startLoading();
      const email: string = formValues.email;
      const firstName: string = formValues.firstName;
      const lastName: string = formValues.lastName;
      const dob: string = formValues.dob;

      const emailStatus = await isEmailExist(email);
      if (emailStatus.isEmailExist) {
        const { isVerified, userId, accountType } = emailStatus;
        if (isVerified === "0" && accountType === "entity") {
          // This email belongs to an unverified business (entity) account.
          // It can never be completed through the personal flow, so offer
          // to resume the business registration instead.
          setConflictEmail(email);
          setBusinessAccountConflict(true);
        } else if (isVerified === "0") {
          dispatch(setfirstName(firstName));
          dispatch(setlastName(lastName));
          dispatch(setRegistrationDob(dob));
          dispatch(setEmail(email));
          dispatch(setUserID(userId));
          await registerMiniOverridePassword(email);
          dispatch(setwizardPageNumber(page + 2));
          dispatch(setWizardStepValidation({ step: page + 2, isValid: true }));
        } else if (entityId || invitationId) {
          // Already-verified identity trying to gain an additional entity
          // relationship (employee via invitationId, or client via entityId).
          const { data, error } = await checkExistingRelationship(userId, email, {
            entityId: entityId || undefined,
            invitationId: invitationId || undefined,
          });

          if (error) {
            notifyError(error);
          } else if (data?.alreadyLinked) {
            setAlreadyLinkedName(data.entityName || "this business");
          } else if (data) {
            setLinkFlow({ userId, email, entityName: data.entityName });
          }
        } else {
          setEmailValidation(true);
        }
      } else {
        dispatch(setEmail(email));
        dispatch(setfirstName(firstName));
        dispatch(setlastName(lastName));
        dispatch(setRegistrationDob(dob));
        dispatch(setwizardPageNumber(page + 1));
        dispatch(setWizardStepValidation({ step: page, isValid: true }));
      }
    } catch (error: any) {
      console.error("Submission Error:", error);
      notifyError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
      setIsSubmitted(false);
    }
  };

  if (linkFlow) {
    return (
      <LinkEntityVerification
        userId={linkFlow.userId}
        email={linkFlow.email}
        entityName={linkFlow.entityName}
        entityId={entityId || undefined}
        invitationId={invitationId || undefined}
        relationshipNoun={relationshipNoun}
        onBack={() => setLinkFlow(null)}
      />
    );
  }

  return (
    <>
      <ToastBox />
      <Container fluid className="px-0 flex-grow-1 d-flex flex-column">
        <div className="row g-0 flex-grow-1">
          <Col md={5} className="px-3 px-md-5 py-4">
            {entityId ? (
              <>
                <h4 className="text-primary mt-3 mb-2 fw-semibold">
                  {t('register.create_personal_title')}
                </h4>
                <h4 className="mt-3 mb-4">
                  <span className="fw-bold">{entityName}</span>
                </h4>
              </>
            ) : (
              <h4 className="text-primary mt-3 mb-2 fw-semibold">
                {t('register.create_personal_title')}
              </h4>
            )}

            <Form noValidate={true} onSubmit={handleSubmit(submitForm)}>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">
                  {t('register.email')}
                </label>
                <Field
                  name="email"
                  type="email"
                  component={renderField}
                  className="form-control rounded-5 py-2"
                  placeholder={t('register.enter_email')}
                  id="email"
                  validate={validateEmail}
                  onChange={handleChangeEmail}
                />
                {emailValidation && (
                  <p className="text-danger text-start small fw-bold">
                    {t('register.email_exists_login')}{" "}
                    <Link className="text-primary" to={"/onboardingLoginAccount"}>
                      {t('register.log_in_instead')}
                    </Link>
                  </p>
                )}
                {alreadyLinkedName && (
                  <p className="text-danger text-start small fw-bold">
                    {t('register.already_linked', { relationship: relationshipNoun, entityName: alreadyLinkedName })}
                  </p>
                )}
                {businessAccountConflict && (
                  <p className="text-danger text-start small fw-bold">
                    {t('register.business_account_exists')}{" "}
                    <Link
                      className="text-primary"
                      to={"/onboardingEntityAccount"}
                      onClick={() => dispatch(setEntityEmail(conflictEmail))}
                    >
                      {t('register.resume_business_registration')}
                    </Link>
                    {" "}{t('register.or_different_email')}
                  </p>
                )}
              </div>
              <div className="mb-3 row">
                <div className="col-md-6">
                  <label htmlFor="firstName" className="form-label">
                    {t('register.first_name')}
                  </label>
                  <Field
                    name="firstName"
                    type="text"
                    component={renderField}
                    className="form-control rounded-5 py-2"
                    placeholder={t('register.enter_first_name')}
                    id="firstName"
                  />
                  <div className="text-danger">
                    <Field name="firstName" component="span" />
                  </div>
                </div>
                <div className="col-md-6">
                  <label htmlFor="lastName" className="form-label">
                    {t('register.last_name')}
                  </label>
                  <Field
                    name="lastName"
                    type="text"
                    component={renderField}
                    className="form-control rounded-5 py-2"
                    placeholder={t('register.enter_last_name')}
                    id="lastName"
                  />
                  <div className="text-danger">
                    <Field name="lastName" component="span" />
                  </div>
                </div>
              </div>

              <div className="mb-3 row">
                <div className="col-md-9">
                  <label htmlFor="dob" className="form-label">
                    {t('register.date_of_birth')}
                  </label>
                  <Field name="dob" component={DobField} id="dob" />
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="refid" className="form-label">
                  {t('register.referral_id')}
                </label>
                <input
                  type="text"
                  className="form-control rounded-5 py-2 prevent-validation"
                  placeholder={t('register.insert_code')}
                  id="refid"
                />
              </div>

              <Form.Check
                checked={isChecked}
                onChange={handleCheckboxChange}
                className={`text-secondary small ${!isChecked && isSubmitted ? "reg-checkbox border-danger" : ""}`}
                label={
                  <span>
                    {t('common.terms_agree')}{" "}
                    <Link className="text-primary" to={"/terms-of-service"} target="_blank">
                      {t('common.terms_of_service')}
                    </Link>{" "}
                    {t('common.and')}{" "}
                    <Link className="text-primary" to={"/privacy-policy"} target="_blank">
                      {t('common.privacy_policy')}
                    </Link>
                    .
                  </span>
                }
              />
              <div className="form-check mb-2">
                <input type="checkbox" className="form-check-input" id="check2" />
                <label className="form-check-label text-secondary small" htmlFor="check2">
                  {t('common.marketing_agree')}
                </label>
              </div>

              <div className="mb-2">
                <ReCAPTCHA
                  ref={recaptcha}
                  sitekey={site_key || ""}
                  onChange={handleRecaptchaChange}
                />
              </div>

              {(!isChecked || validated) && isSubmitted && (
                <p className="text-danger text-start small fw-bold">
                  {!isChecked
                    ? t('register.terms_required')
                    : t('register.recaptcha_required')}
                </p>
              )}

              <Button
                variant="primary"
                type="submit"
                className="btn btn-primary rounded-5 mt-2"
                disabled={loading || !captchaValue || !isChecked || emailValidation || businessAccountConflict}
              >
                {loading && (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="mx-2"
                  />
                )}
                <span>{t('register.create_personal_title')}</span>
              </Button>
            </Form>

            {!entityId && !invitationId && (
              <Row className="mt-4 mb-5">
                <Col md={12}>
                  <span>
                    {t('register.not_personal')}
                    <Link className="text-primary px-2" to={"/onboardingEntityAccount"}>
                      {t('register.register_entity_link')}
                    </Link>
                  </span>
                </Col>
              </Row>
            )}
          </Col>

          <Col
            md={7}
            className={`d-none d-md-flex flex-column align-items-center justify-content-center text-center py-5 ${theme === "light" ? "bg-sky" : "bg-dark-solid shadow-sm"}`}
          >
            <img src={signup_banner} className="img-fluid" style={{ maxWidth: 500, width: "100%" }} alt="banner" />
            <h2 className="trade-confidence mt-4">{t('common.all_in_one')}</h2>
            <p className="text-secondary">{t('common.all_in_one_desc')}</p>
          </Col>
        </div>
      </Container>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  initialValues: {
    firstName: state.auth.firstName || "",
    lastName: state.auth.lastName || "",
    email: state.auth.email || "",
    dob: state.auth.registrationDob || "",
    refferalId: state.auth.entityId || "",
  },
});

const validate = (values: any) => {
  const errors: any = {};
  if (!values.firstName) {
    errors.firstName = "Please enter your first name";
  }
  if (!values.lastName) {
    errors.lastName = "Please enter your last name";
  }
  if (!values.dob) {
    errors.dob = "Please enter your date of birth";
  } else {
    const dobDate = new Date(values.dob);
    const today = new Date();
    if (isNaN(dobDate.getTime())) {
      errors.dob = "Please enter a valid date of birth";
    } else if (dobDate > today) {
      errors.dob = "Date of birth cannot be in the future";
    } else if (today.getFullYear() - dobDate.getFullYear() > 120) {
      errors.dob = "Please enter a valid date of birth";
    }
  }
  return errors;
};

const form = reduxForm<FormData, Props>({
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate,
  form: "wizard",
  enableReinitialize: true,
})(RegisterPersonalAccount);

export default connect(mapStateToProps)(form);
